import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import { compose, graphql } from "react-apollo";
import { gql } from "apollo-boost";
import { Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import SendIcon from "@material-ui/icons/Send";

import { API_URL, ENV } from "../../config";

const useStyles = makeStyles(theme => ({
  buttonWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  clause: {
    fontSize: "0.75em",
    lineHeight: "1.5"
  },
  extendedIcon: {
    marginRight: theme.spacing(1)
  },
  label: {
    fontSize: "1em"
  },
  table: {
    minWidth: 650
  },
  textField: {
    "& > label > span.MuiInputLabel-asterisk": {
      color: "red"
    }
  },
  asterix: {
    color: "red"
  }
}));

const STATUS = {
  error: "error",
  completed: "completed",
  processing: "processing"
};

function handleSubmitValuation({
  addOrder,
  items,
  locale,
  onError,
  onSendClick,
  values,
  setStatus,
  setOpen,
  setSubmitting
}) {
  const { comments, country, name, lastName, phone, email, zipCode } = values;
  // const addedAt = getCurrentTime();
  // const locales = LOCALES;
  setStatus(STATUS.processing);

  if (onSendClick) onSendClick();

  const addedAt = dayjs().format("YYYY-MM-DD HH:mm:ssZ");
  const type = process.env.ENV === "production" ? "prod" : "dev";

  addOrder({
    variables: {
      comments,
      name,
      lastName,
      phone,
      email,
      zipCode,
      country,
      locale,
      type,
      addedAt
    }
  })
    .then(({ data: { addOrder: { id: orderId } } }) => {
      return axios.post(`${API_URL}/api/send_email`, {
        form: { comments, country, name, lastName, phone, email, zipCode },
        items,
        locale,
        orderId
      });
    })
    .then(function(response) {
      setStatus(STATUS.completed);
    })
    .catch(function(error) {
      setStatus(STATUS.error);
      console.error(error);
      if (onError) onError(error);
    })
    .then(function() {
      // always executed
    });
}

function OrderForm({
  addOrder,
  items,
  locale,
  onError,
  onOpenClick,
  onSendClick
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [clauseExpanded, setClauseExpanded] = useState(false);

  const validationSchema = Yup.object().shape({
    comments: Yup.string(),
    name: Yup.string().required(t("order.form.required.name")),
    lastName: Yup.string().required(t("order.form.required.name")),
    phone: Yup.string().required(t("order.form.required.name")),
    email: Yup.string()
      .email(t("order.form.invalid.email"))
      .required(t("order.form.required.name")),
    terms: Yup.bool().oneOf([true], t("order.form.required.terms")),
    zipCode: Yup.string().required(t("order.form.required.name")),
    country: Yup.string().required(t("order.form.required.country"))
  });

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        type="submit"
        onClick={() => {
          setOpen(true);
          if (onOpenClick) onOpenClick();
        }}
        disabled={!items.length}
      >
        <SendIcon className={classes.extendedIcon} fontSize="small" />
        {t("order.send to valuation")}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="form-dialog-title"
      >
        <Formik
          initialValues={{
            comments: "",
            name: "",
            lastName: "",
            phone: "",
            email: "",
            terms: false,
            zipCode: "",
            country: ""
          }}
          onSubmit={(values, { setSubmitting }) =>
            handleSubmitValuation({
              addOrder,
              items,
              locale,
              onError,
              onSendClick,
              values,
              setStatus,
              setOpen,
              setSubmitting
            })
          }
          validationSchema={validationSchema}
        >
          {props => {
            const {
              values,
              touched,
              errors,
              isSubmitting,
              handleBlur,
              handleChange,
              handleSubmit
            } = props;
            // TODO: Handle error state
            return (
              <form onSubmit={handleSubmit}>
                {/* <DialogTitle id="form-dialog-title">
                  {t(
                    status === STATUS.completed
                      ? "order.order placed"
                      : "order.place order"
                  )}
                </DialogTitle> */}
                <DialogContent>
                  {status === STATUS.completed ? (
                    <DialogContentText>
                      {t("order.form.completed")}
                    </DialogContentText>
                  ) : (
                    <>
                      <DialogContentText>
                        {t("order.form.description")}
                      </DialogContentText>
                      <div>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="comments"
                            type="text"
                            label={t("order.form.comments")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.comments}
                            onChange={handleChange}
                            margin="normal"
                            helperText={errors.comments}
                            fullWidth
                            multiline
                            rows={8}
                          />
                        </Box>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="name"
                            type="text"
                            label={t("order.form.name")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.name}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.name && touched.name}
                            helperText={errors.name}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="lastName"
                            type="text"
                            label={t("order.form.last name")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.lastName}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.lastName && touched.lastName}
                            helperText={errors.lastName}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="phone"
                            type="text"
                            label={t("order.form.phone")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.phone}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.phone && touched.phone}
                            helperText={errors.phone}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="email"
                            type="text"
                            label={t("order.form.email")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.email}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.email && touched.email}
                            helperText={errors.email}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="zipCode"
                            type="text"
                            label={t("order.form.zip code")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.zipCode}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.zipCode && touched.zipCode}
                            helperText={errors.zipCode}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <TextField
                            className={classes.textField}
                            id="country"
                            type="text"
                            label={t("order.form.country")}
                            InputLabelProps={{
                              shrink: true
                            }}
                            variant="outlined"
                            value={values.country}
                            onChange={handleChange}
                            margin="normal"
                            error={errors.country && touched.country}
                            helperText={errors.country}
                            fullWidth
                            required
                          />
                        </Box>
                        <Box>
                          <div className={classes.clause}>
                            <ExpansionPanel
                              expanded={clauseExpanded}
                              onChange={(_, newExpanded) =>
                                setClauseExpanded(newExpanded)
                              }
                            >
                              <ExpansionPanelSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                              >
                                <FormControl
                                  required
                                  error={errors.terms && touched.terms}
                                  component="fieldset"
                                  // className={classes.formControl}
                                >
                                  <FormControlLabel
                                    classes={{ label: classes.label }}
                                    control={
                                      <Checkbox
                                        checked={values.terms}
                                        onChange={handleChange}
                                        name="terms"
                                        color="primary"
                                        size="small"
                                        required
                                      />
                                    }
                                    required
                                    label={
                                      <>
                                        {t("order.form.terms")}{" "}
                                        <span className={classes.asterix}>
                                          *
                                        </span>
                                      </>
                                    }
                                  />
                                  <FormHelperText>
                                    {errors.terms}
                                  </FormHelperText>
                                </FormControl>
                              </ExpansionPanelSummary>
                              <ExpansionPanelDetails>
                                <div>
                                  {t("order.form.clause.title")}
                                  <ol>
                                    <li>{t("order.form.clause.list.1")}</li>
                                    <li>{t("order.form.clause.list.2")}</li>
                                    <li>{t("order.form.clause.list.3")}</li>
                                    <li>{t("order.form.clause.list.4")}</li>
                                    <li>{t("order.form.clause.list.5")}</li>
                                    <li>{t("order.form.clause.list.6")}</li>
                                    <li>{t("order.form.clause.list.7")}</li>
                                    <li>{t("order.form.clause.list.8")}</li>
                                  </ol>
                                </div>
                              </ExpansionPanelDetails>
                            </ExpansionPanel>
                          </div>
                        </Box>
                      </div>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpen(false)} color="primary">
                    {t("order.exit")}
                  </Button>
                  {status !== STATUS.completed ? (
                    <div className={classes.buttonWrapper}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        required
                      >
                        {t("order.send")}
                      </Button>
                      {status === STATUS.processing && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </div>
                  ) : null}
                </DialogActions>
              </form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
}

export const addOrderMutation = gql`
  mutation(
    $comments: String
    $name: String!
    $lastName: String!
    $email: String!
    $phone: String!
    $zipCode: String!
    $country: String
    $locale: String!
    $type: String!
    $addedAt: String!
  ) {
    addOrder(
      comments: $comments
      name: $name
      lastName: $lastName
      email: $email
      phone: $phone
      zipCode: $zipCode
      country: $country
      locale: $locale
      type: $type
      addedAt: $addedAt
    ) {
      id
    }
  }
`;

const withData = compose(
  graphql(addOrderMutation, {
    name: "addOrder"
  })
);

export default withData(OrderForm);
